import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Search, Trash } from 'lucide-react'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

interface OrganizerValidatorManagementProps {
  organizerId: number | null
  validators: Array<{ user_id: string; email: string }>
  onRefresh: () => void
}

export default function OrganizerValidatorManagement({ organizerId, validators, onRefresh }: OrganizerValidatorManagementProps) {
  const [validatorEmail, setValidatorEmail] = useState('')
  const [suggestions, setSuggestions] = useState<Array<{ user_id: string; email: string; role: string }>>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [rolesByUser, setRolesByUser] = useState<Record<string, string>>({})
  const [events, setEvents] = useState<Array<{ id: number; title: string }>>([])
  const [showEventSelect, setShowEventSelect] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const [pendingAssignEmail, setPendingAssignEmail] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const suggestionsRef = useRef<HTMLDivElement | null>(null)
  const searchBtnRef = useRef<HTMLButtonElement | null>(null)
  const [eventValidators, setEventValidators] = useState<Array<{ screening_id: number; user_id: string; email: string; name: string; title: string; active: boolean }>>([])
  const [loadingOps, setLoadingOps] = useState<Record<string, boolean>>({})
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ screening_id: number; email: string; user_id: string } | null>(null)

  const runSearch = async (term?: string) => {
    const v = (term ?? validatorEmail).trim()
    if (v.length < 2) { setShowSuggestions(false); setSuggestions([]); return }
    const { data, error } = await supabase.rpc('search_users_for_organizer', { term: v, limit_count: 8 })
    console.log('Search users result:', { term: v, data, error })
    if (error) {
      console.error('Search users error:', error)
      toast.error('Search failed')
      setShowSuggestions(false)
      setSuggestions([])
      return
    }
    const rows = (data || []) as Array<{ user_id: string; email: string; role: string | null }>
    const mapped = rows.map(d => ({ user_id: d.user_id, email: d.email, role: (d.role || '') }))
    setSuggestions(mapped)
    setShowSuggestions(mapped.length > 0)
  }

  const assignValidator = async (enable: boolean) => {
    if (!validatorEmail) {
      toast.error('Enter an email')
      return
    }
    try {
      const { error } = await supabase.rpc('assign_validator_by_email', { target_email: validatorEmail, organizer_id: organizerId, enable })
      if (error) {
        toast.error('Failed to update validator')
        return
      }
      toast.success(enable ? 'Validator assigned' : 'Validator removed')
      setValidatorEmail('')
      await refreshRoles()
      onRefresh()
    } catch {
      toast.error('Failed to update validator')
    }
  }

  const assignValidatorByEmail = async (email: string) => {
    if (!email) return
    try {
      const { error } = await supabase.rpc('assign_validator_by_email', { target_email: email, organizer_id: organizerId, enable: true })
      if (error) {
        toast.error('Failed to update validator')
        return
      }
      toast.success('Validator assigned')
      setValidatorEmail('')
      setShowSuggestions(false)
      await refreshRoles()
      onRefresh()
    } catch {
      toast.error('Failed to update validator')
    }
  }

  const refreshRoles = async () => {
    try {
      const ids = validators.map(v => v.user_id)
      if (!ids.length) { setRolesByUser({}); return }
      const { data } = await supabase
        .from('profiles')
        .select('id, role')
        .in('id', ids)
      const map: Record<string, string> = {}
      for (const r of ((data || []) as { id: string; role: string | null }[])) {
        map[r.id] = r.role || ''
      }
      setRolesByUser(map)
    } catch {
      setRolesByUser({})
    }
  }

  const refreshEvents = async () => {
    try {
      if (!organizerId) { setEvents([]); return }
      const { data: ms } = await supabase
        .from('movies')
        .select('id,title')
        .eq('organizer_id', organizerId)
      const mRows = ((ms || []) as { id: number; title: string }[])
      const mIds = mRows.map(m => m.id)
      if (!mIds.length) { setEvents([]); return }
      const titleMap = new Map<number, string>(mRows.map(m => [m.id, m.title]))
      const { data: ss } = await supabase
        .from('screenings')
        .select('id,movie_id,start_time')
        .in('movie_id', mIds)
        .gte('start_time', new Date().toISOString())
        .order('start_time', { ascending: true })
      const evs = ((ss || []) as { id: number; movie_id: number }[]).map(s => ({ id: s.id, title: titleMap.get(s.movie_id) || '' }))
      setEvents(evs)
      const sIds = evs.map(e => e.id)
      if (!sIds.length) { setEventValidators([]); return }
      const { data: details, error } = await supabase.rpc('get_screening_validators_details', { screening_ids: sIds })
      if (error) { setEventValidators([]); return }
      const titleByScreening = new Map<number, string>(evs.map(e => [e.id, e.title]))
      const rows = (details || []) as { screening_id: number; user_id: string; email: string; name: string; active: boolean }[]
      setEventValidators(rows.map(r => ({ screening_id: r.screening_id, user_id: r.user_id, email: r.email || '', name: r.name || '', title: titleByScreening.get(r.screening_id) || '', active: !!r.active })))
    } catch {
      setEvents([])
      setEventValidators([])
    }
  }

  useEffect(() => { refreshRoles() }, [validators])
  useEffect(() => { refreshEvents() }, [organizerId])

  const eventBadges = useMemo(() => events.slice(0, 2), [events])

  const keyOf = (screening_id: number, user_id: string) => `${screening_id}:${user_id}`

  const setLoading = (k: string, v: boolean) => setLoadingOps(prev => ({ ...prev, [k]: v }))

  const toggleEventValidator = async (screening_id: number, email: string, currentActive: boolean, user_id: string) => {
    const k = keyOf(screening_id, user_id)
    setLoading(k, true)
    try {
      const { error } = await supabase.rpc('set_validator_active_for_event', { target_email: email, target_screening_id: screening_id, active: !currentActive })
      if (error) { toast.error('Failed to update status'); return }
      setEventValidators(prev => prev.map(ev => (ev.screening_id === screening_id && ev.user_id === user_id) ? { ...ev, active: !currentActive } : ev))
      toast.success(!currentActive ? 'Validator activated' : 'Validator deactivated')
    } catch {
      toast.error('Failed to update status')
    } finally {
      setLoading(k, false)
    }
  }

  const removeEventValidator = async (screening_id: number, email: string, user_id: string) => {
    const k = keyOf(screening_id, user_id)
    setLoading(k, true)
    try {
      const { error } = await supabase.rpc('remove_validator_from_event', { target_email: email, target_screening_id: screening_id })
      if (error) { toast.error('Failed to remove validator'); return }
      setEventValidators(prev => prev.filter(ev => !(ev.screening_id === screening_id && ev.user_id === user_id)))
      toast.success('Validator removed')
    } catch {
      toast.error('Failed to remove validator')
    } finally {
      setLoading(k, false)
      setConfirmDeleteOpen(false)
      setDeleteTarget(null)
    }
  }

  const toggleValidator = async (email: string, enable: boolean) => {
    try {
      const { error } = await supabase.rpc('assign_validator_by_email', { target_email: email, organizer_id: organizerId, enable })
      if (error) {
        toast.error('Failed to update validator')
        return
      }
      toast.success(enable ? 'Validator enabled' : 'Validator disabled')
      await refreshRoles()
      onRefresh()
    } catch {
      toast.error('Failed to update validator')
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
        <h2 className="text-gray-900 dark:text-white text-lg font-bold">Assign New Validator</h2>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-4">
          <label className="flex flex-col min-w-40 flex-1">
            <p className="text-gray-700 dark:text-gray-300 text-sm font-medium pb-2">Validator's Email</p>
            <div className="relative">
              <input
                ref={inputRef}
                className="form-input w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-[12px] text-base text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-indigo-500 focus:outline-0 focus:ring-0 h-12"
                placeholder="e.g., validator@example.com"
                value={validatorEmail}
                onChange={async e => {
                  const v = e.target.value
                  setValidatorEmail(v)
                  if (v.length >= 2) {
                    await runSearch(v)
                  } else {
                    setShowSuggestions(false)
                    setSuggestions([])
                  }
                }}
                onFocus={() => setShowSuggestions(suggestions.length > 0)}
                onBlur={(e) => {
                  const t = e.relatedTarget as Node | null
                  const keep = (t && (suggestionsRef.current?.contains(t) || searchBtnRef.current?.contains(t))) || false
                  if (!keep) setShowSuggestions(false)
                }}
                onKeyDown={e => { if (e.key === 'Enter' && validatorEmail) runSearch() }}
              />
              {showEventSelect && (
                <div className="absolute mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                  <div className="px-3 py-2">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Select Event</p>
                    <select
                      className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded px-3 py-2 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-indigo-500"
                      value={selectedEventId ?? ''}
                      onChange={e => setSelectedEventId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Choose an event</option>
                      {events.map(ev => (
                        <option key={ev.id} value={ev.id}>{ev.title}</option>
                      ))}
                    </select>
                    <div className="mt-3 flex gap-2 justify-end">
                      <button
                        type="button"
                        className="px-3 py-1 rounded bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 hover:dark:bg-gray-600"
                        onClick={() => { setShowEventSelect(false); setPendingAssignEmail(null); setSelectedEventId(null) }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                        onClick={async () => {
                          if (!pendingAssignEmail) return
                          if (!selectedEventId) { toast.error('Please select an event'); return }
                          const { error } = await supabase.rpc('assign_validator_to_event', { target_email: pendingAssignEmail, target_screening_id: selectedEventId, enable: true })
                          if (error) { toast.error('Failed to assign validator'); return }
                          try {
                            await supabase.from('notifications').insert([
                              {
                                actor_id: null,
                                actor_email: null,
                                entity: 'screening',
                                entity_id: selectedEventId,
                                action: 'validator assigned',
                                message: `Validator ${pendingAssignEmail} assigned to event #${selectedEventId}`,
                                details: { validator_email: pendingAssignEmail, screening_id: selectedEventId }
                              }
                            ])
                          } catch { }
                          toast.success('Validator assigned')
                          setShowEventSelect(false)
                          setPendingAssignEmail(null)
                          setSelectedEventId(null)
                          await refreshRoles()
                          onRefresh()
                        }}
                      >
                        Assign to Event
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showSuggestions && (
                <div ref={suggestionsRef} className="absolute mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                  {suggestions.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">No users found</div>
                  )}
                  {suggestions.filter(s => s.role !== 'admin' && s.role !== 'organizer').map(s => (
                    <div key={s.user_id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <button
                        type="button"
                        onMouseDown={() => {
                          setValidatorEmail(s.email)
                          setShowSuggestions(false)
                          setPendingAssignEmail(s.email)
                          setShowEventSelect(true)
                        }}
                        className="text-left"
                      >
                        <span className="text-gray-900 dark:text-white">{s.email.split('@')[0]}</span>
                        <span className="text-gray-600 dark:text-gray-400 ml-2">· {s.email}</span>
                        {s.role && <span className="text-gray-600 dark:text-gray-400 ml-2">· {s.role}</span>}
                      </button>
                      <button
                        type="button"
                        onMouseDown={() => { setPendingAssignEmail(s.email); setShowEventSelect(true) }}
                        className="px-3 py-1 bg-indigo-600 text-white rounded"
                      >
                        Assign
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </label>
          <button
            ref={searchBtnRef}
            onClick={() => runSearch()}
            className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-5 bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
          >
            <Search className="h-4 w-4 mr-2" />
            Search
          </button>
        </div>
      </div>

      <section className="flex flex-col gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="grid grid-cols-[2fr_2fr_1fr_auto] gap-4 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Validator</h3>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Event</h3>
          <div className="flex items-center gap-1">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Status</h3>
          </div>
          <div className="w-10" />
        </div>
        {eventValidators.length === 0 ? (
          <div className="px-6 py-4">
            <p className="text-gray-700 dark:text-gray-400">No validators</p>
          </div>
        ) : (
          eventValidators.map(ev => {
            const k = keyOf(ev.screening_id, ev.user_id)
            const busy = !!loadingOps[k]
            return (
              <div key={k} className="grid grid-cols-[2fr_2fr_1fr_auto] items-center gap-4 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-gray-900 dark:text-white">{ev.name}</span>
                  <span className="truncate text-xs text-gray-600 dark:text-gray-400">{ev.email}</span>
                </div>
                <p className="truncate text-sm font-medium text-gray-700 dark:text-gray-300">{ev.title || 'Untitled Event'}</p>
                <div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      disabled={busy}
                      checked={ev.active}
                      onChange={() => toggleEventValidator(ev.screening_id, ev.email, ev.active, ev.user_id)}
                      className="peer sr-only"
                      type="checkbox"
                    />
                    <div className={`peer h-6 w-11 rounded-full ${ev.active ? 'bg-indigo-600' : 'bg-gray-600'} after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-500 after:bg-gray-800 after:transition-all after:content-[''] peer-checked:bg-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none`}></div>
                  </label>
                </div>
                <button
                  disabled={busy}
                  onClick={() => { setDeleteTarget({ screening_id: ev.screening_id, email: ev.email, user_id: ev.user_id }); setConfirmDeleteOpen(true) }}
                  title={busy ? 'Working...' : ''}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg ${busy ? 'opacity-50 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'}`}
                >
                  <Trash className="h-4 w-4" />
                </button>
              </div>
            )
          })
        )}
      </section>

      {confirmDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 bg-black/60 z-[120] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Confirm Removal</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">Are you sure you want to remove this validator from the event?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setConfirmDeleteOpen(false); setDeleteTarget(null) }}
                className="px-4 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white rounded hover:dark:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => removeEventValidator(deleteTarget.screening_id, deleteTarget.email, deleteTarget.user_id)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
